import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, arrayOf, shape, extend, customType, number
} from '@thd-nucleus/data-sources';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  ProgressBar,
  Typography
} from '@one-thd/sui-atomic-components';
import { AddToContext } from '../../AddToContext';
import { PRODUCTS_LOADING_MESSAGE, MAX_PI_QUERY_SIZE } from '../../AddToConstants';
import { useProducts } from '../../hooks/useProducts';
import { useQuoteProduct } from '../../hooks/useQuoteProduct';
import { AddToProductPod } from '../addto-product-pod/AddToProductPod';

export const LoadingStatus = ({
  products,
  loadingProgress,
  projectDetails,
  loadedProductsData,
  updateLoadedProductsData
}) => {
  const [page, setPage] = useState(1);
  const [quoteProductPage, setQuoteProductPage] = useState(1);
  const { handleClose } = useContext(AddToContext);
  const { storeId: projectStoreId, zipCode: projectZipCode, projectId } = projectDetails || {};

  const isLoading = loadingProgress !== 100;

  const numberOfItems = products?.length ?? 0;

  const {
    fetchProducts
  } = useProducts({ updateLoadedProductsData, loadedProductsData, setPage, projectId });

  const {
    fetchNonSharedQcProductData
  } = useQuoteProduct({
    projectStoreId, projectZipCode, loadedProductsData, updateLoadedProductsData, setQuoteProductPage
  });

  const filteredNonSharedQcProducts = useMemo(() => {
    return products.filter((product) => product?.itemType === 'qc-bridge-catalog-non-shared');
  }, [products]);

  const getNonSharedQcProducts = () => {
    const startIndex = (quoteProductPage - 1);
    const slicedItem = filteredNonSharedQcProducts.slice(startIndex, startIndex + 1);
    if (slicedItem?.length > 0) {
      fetchNonSharedQcProductData(slicedItem[0]);
    }
  };

  const getProductInformation = () => {
    const startIndex = (page - 1) * MAX_PI_QUERY_SIZE;
    const slicedItems = products.slice(startIndex, startIndex + MAX_PI_QUERY_SIZE);
    if (slicedItems?.length > 0) {
      fetchProducts(slicedItems);
    }
  };

  useEffect(() => {
    getProductInformation();
    getNonSharedQcProducts();
  }, []);

  useEffect(() => {
    if (page !== 1 && isLoading) {
      getProductInformation();
    }
    if (quoteProductPage !== 1 && isLoading) {
      getNonSharedQcProducts();
    }
  }, [page, quoteProductPage, loadingProgress]);

  return (
    <>
      <DrawerBody>
        <div className="sui-min-h-screen">
          <Typography variant="body-lg" weight="bold">Hang Tight!</Typography>
          <Typography variant="body-base">
            {PRODUCTS_LOADING_MESSAGE}
          </Typography>
          <div className="sui-pt-8">
            <ProgressBar
              status={loadingProgress === 100 ? 'success' : undefined}
              value={loadingProgress}
              startLabel={`${numberOfItems} items`}
              endLabel={`${loadingProgress}%`}
            />
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          fullWidth
          onClick={handleClose}
          variant="secondary"
        >
          Cancel
        </Button>
      </DrawerFooter>
    </>
  );
};

LoadingStatus.displayName = 'LoadingStatus';

LoadingStatus.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    itemType: PropTypes.string,
    id: PropTypes.string,
    sku: PropTypes.string,
    bridgeSolution: PropTypes.shape({
      optionKey: PropTypes.string,
      qcId: PropTypes.string
    })
  })).isRequired,
  loadingProgress: PropTypes.number.isRequired,
  projectDetails: PropTypes.shape({}).isRequired,
  loadedProductsData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updateLoadedProductsData: PropTypes.func.isRequired
};

LoadingStatus.dataModel = extend({
  quoteProduct: params({
    accountId: string(),
    store: string(),
    deliveryAddress: customType('DeliveryAddress').shape({
      zipCode: string()
    }),
    itemDetail: customType('ItemDetail').shape({
      omsId: string(),
      qcId: string(),
      sku: string(),
      upc: string(),
      optionKey: string(),
      quantity: number()
    })
  }).mutation().shape({
    product: shape({
      description: string(),
      uomAbbreviation: string(),
      sku: string(),
      imageGroups: arrayOf(shape({
        images: arrayOf(shape({
          url: string(),
          dimensions: shape({
            width: string(),
            height: string()
          })
        })),
        description: string()
      }))
    }),
    pricing: shape({
      effectivePrice: number(),
      originalPrice: number()
    })
  }),
  products: params({
    itemIds: arrayOf(string().isRequired()).isRequired(),
    dataSource: string(),
    projectId: string(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: string(),
      programTiers: arrayOf(
        shape({
          tier: string(),
          program: string()
        })
      )
    })
  }).arrayOf(AddToProductPod.dataModel.product),
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      brandName: string(),
      productLabel: string(),
      storeSkuNumber: string()
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string()
      }))
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string()
      })
    })
  })
},
AddToProductPod
);